import { useState, useEffect, useMemo } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/molecules/Table'
import { Button } from '@/components/atoms/Button'
import { FieldRenderer } from '@/components/molecules/fieldRenderer'
import { toast } from '@/hooks/useToast'
import { DEFAULT_ADMIN_FILE_INPUT_ACCEPT } from '@/constants/fileUpload'

interface FieldConfig {
  header: string
  fieldName: string
  value?: any
  valueType?: string
  filedType: string
  placeholder?: string
  required?: boolean
  validation?: (value: any) => string | null
  linkedField?: string
  linkedValueKey?: string
  options?: Array<{ value: string; label: string; [key: string]: any }>
  defaultValue?: any
  [key: string]: any
}

interface TableData {
  [key: string]: any
}

interface TableFormatterProps {
  data?: TableData[]
  fields?: FieldConfig[]
  onDataUpdate?: (updatedData: TableData[]) => void
  validationErrors?: Record<string, boolean>
  validationMessages?: Record<string, string>
  /** Applied to FILE columns without an explicit `accept` (e.g. video uploads). */
  fileAccept?: string
  /** Optional row-count limits. When set, "Add New Row" is capped at `maxRows`
   * and a guidance note is shown. Save-time enforcement lives in Form1's hook. */
  minRows?: number
  maxRows?: number
}

const PRIORITY_FIELD_NAMES = ['order_index', 'order']
const DEFAULT_LOADING_STATE = false

const isPriorityField = (fieldName: string): boolean => {
  return fieldName.toLowerCase().includes('priority') || PRIORITY_FIELD_NAMES.includes(fieldName)
}

const getDefaultValueByType = (valueType?: string): any => {
  switch (valueType) {
    case 'string':
      return ''
    case 'number':
      return 0
    case 'boolean':
      return false
    default:
      return ''
  }
}

const TableFormatter = ({
  data = [],
  fields = [],
  onDataUpdate,
  validationErrors = {},
  validationMessages = {},
  fileAccept = DEFAULT_ADMIN_FILE_INPUT_ACCEPT,
  minRows,
  maxRows,
}: TableFormatterProps) => {
  const [tableData, setTableData] = useState<TableData[]>([])
  const [nextId, setNextId] = useState(1)

  const atMax = maxRows != null && tableData.length >= maxRows
  const hasLimits = minRows != null || maxRows != null
  const limitNote =
    minRows != null && maxRows != null
      ? `Add between ${minRows} and ${maxRows} items.`
      : maxRows != null
        ? `Add up to ${maxRows} items.`
        : minRows != null
          ? `Add at least ${minRows} items.`
          : ""

  const fieldsWithFileAccept = useMemo(() => {
    if (!fileAccept) return fields
    return fields.map((f) =>
      f.filedType === 'FILE' && f.accept == null
        ? { ...f, accept: fileAccept }
        : f
    )
  }, [fields, fileAccept])

  const tableColumnFields = useMemo(
    () => fieldsWithFileAccept.filter((f) => f.filedType !== 'TEXT_BLOCK'),
    [fieldsWithFileAccept],
  )

  useEffect(() => {
    setTableData(data)
    const maxId = data.reduce((max, row) => {
      const id = parseInt(row.id) || 0
      return id > max ? id : max
    }, 0)
    setNextId(maxId + 1)
  }, [data])

  const getNextPriorityValue = (fieldName: string): number => {
    const maxPriority = tableData.reduce((max, row) => {
      const value = parseInt(row[fieldName]) || 0
      return value > max ? value : max
    }, 0)
    return maxPriority + 1
  }

  const handleChange = (rowId: string, fieldName: string, value: any, field?: FieldConfig) => {
    const updatedData = tableData.map((row, index) => {
      if (getRowKey(row, index) !== rowId) return row

      const updatedRow = { ...row, [fieldName]: value }

      if (field?.linkedField && field?.linkedValueKey && field?.options) {
        const selectedOption = field.options.find(opt => String(opt.value) === String(value))
        if (selectedOption) {
          updatedRow[field.linkedField] = selectedOption[field.linkedValueKey] || ''
        }
      }

      return updatedRow
    })

    setTableData(updatedData)
    onDataUpdate?.(updatedData)
  }

  const handleAddRow = () => {
    if (maxRows != null && tableData.length >= maxRows) {
      toast({
        variant: 'destructive',
        title: 'Limit reached',
        description: `You can add a maximum of ${maxRows} items.`,
      })
      return
    }
    const newRow: TableData = { id: nextId.toString() }

    tableColumnFields.forEach(field => {
      if (isPriorityField(field.fieldName) && field.valueType === 'number') {
        newRow[field.fieldName] = getNextPriorityValue(field.fieldName)
      } else if (field.value !== undefined && field.value !== '') {
        newRow[field.fieldName] = field.value
      } else if (field.defaultValue !== undefined) {
        newRow[field.fieldName] = field.defaultValue
      } else {
        newRow[field.fieldName] = getDefaultValueByType(field.valueType)
      }
    })

    const updatedData = [...tableData, newRow]
    setTableData(updatedData)
    setNextId(prev => prev + 1)
    onDataUpdate?.(updatedData)
  }

  const handleDeleteRow = (rowId: string) => {
    const updatedData = tableData.filter((row, index) => getRowKey(row, index) !== rowId)
    setTableData(updatedData)
    onDataUpdate?.(updatedData)
  }

  const handleAction = async () => {}

  const getRowKey = (rowData: TableData, index: number): string => {
    return rowData.id || rowData.meta_id || `row-${index}`
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-4">
        <Button
          onClick={handleAddRow}
          classType="admin"
          className="px-4 py-2"
          disabled={atMax}
        >
          Add New Row
        </Button>
        {hasLimits && (
          <span className="text-body-4-medium text-gray-5">
            {limitNote}
            {maxRows != null && ` (${tableData.length}/${maxRows})`}
          </span>
        )}
      </div>

      <Table
        className="border border-gray-3"
        scrollContainerClassName="max-h-[min(75vh,52rem)]"
      >
        <TableHeader>
          <TableRow className="bg-gray-2">
            <TableHead className="sticky top-0 z-10 w-16 bg-gray-2 text-gray-6 font-semibold shadow-[0_1px_0_0_rgba(0,0,0,0.06)]">
              #
            </TableHead>
            {tableColumnFields.map(field => (
              <TableHead
                key={field.fieldName}
                className="sticky top-0 z-10 bg-gray-2 text-gray-6 font-semibold shadow-[0_1px_0_0_rgba(0,0,0,0.06)]"
              >
                {field.header}
                {field.required && <span className="text-secondary-main ml-1">*</span>}
              </TableHead>
            ))}
            <TableHead className="sticky top-0 z-10 bg-gray-2 text-gray-6 font-semibold shadow-[0_1px_0_0_rgba(0,0,0,0.06)]">
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tableData.map((rowData, index) => {
            const rowKey = getRowKey(rowData, index)

            return (
              <TableRow key={rowKey} className="hover:bg-gray-1">
                <TableCell className="text-gray-5 font-medium">{index + 1}</TableCell>
                {tableColumnFields.map(field => {
                  const errorKey = `${rowKey}_${field.fieldName}`

                  return (
                    <TableCell key={errorKey}>
                      {FieldRenderer(
                        field,
                        rowData,
                        (fieldName: string, value: any) => handleChange(rowKey, fieldName, value, field),
                        handleAction,
                        DEFAULT_LOADING_STATE,
                        toast,
                        { [field.fieldName]: validationErrors[errorKey] || false },
                        validationMessages[errorKey] || ''
                      )}
                    </TableCell>
                  )
                })}
                <TableCell>
                  <Button
                    onClick={() => handleDeleteRow(rowKey)}
                    classType="outline"
                    className="px-3 py-1 text-body-4-medium text-secondary-main border-secondary-main hover:bg-secondary-main hover:text-white"
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}

export default TableFormatter
